/**
 * Orders
 */

// React
import React, { useEffect, useState, useContext, FC } from 'react';
import { useForm } from 'react-hook-form';

// Context
import AuthContext, { AuthConsumer } from '../../authContext';

// UI
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';

// Icon
import {
  mdiAccountOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
} from '@mdi/js';

// Components
import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import type { Filter } from '../../utils/listDaynamicFilter';
import List from './warehouseAdminList';
import Pagination from '@material-ui/lab/Pagination';
import Can from '../../components/Can';
// Helper
import {
  removeEmptyValueObject,
  translateOrderStatus,
} from '../../utils/helpers';

import useQuery from '../../utils/hook/useQuery';

import handleError from '../../utils/handleError';
import usersRepository from 'client-sdk/src/Repository/users-repository';
import cancellationTasksRepository from 'client-sdk/src/Repository/cancellation-task-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '30px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};

const Orders: FC<Props> = (props: Props): React.ReactElement => {
  const authContext = useContext(AuthContext);
  const { register, handleSubmit, reset } = useForm<Inputs>();
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 100,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [orderUpdate, setOrderUpdate] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [lastUpdate, setLastUpdate] = useState(
    '2019-07-02T17:15:03+04:30' as string
  );
  const [openFilterBox, setOpenFilterBox] = React.useState(false);

  const [ordersList, setOrdersList] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };

  // Bad Bad Bad.. :(
  ordersList[0]?.created &&
    ordersList[0]?.created > lastUpdate &&
    setLastUpdate(ordersList[0]?.created);

  useEffect(() => {
    getOrders();
    setupFilters();
  }, [filtersValues]);

  useEffect(() => {
    fetchUsers();
  }, [orderUpdate]);

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };
  const getAllCancelationTasks = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response =
        await cancellationTasksRepository.getAllCancellationTasksReport({
          payload,
        });
      setAllOrders(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getingCancelatiionTask = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } =
        await cancellationTasksRepository.getAllCancellationTasks({ payload });
      setOrdersList(response.data);
      setCount(count.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getOrders = () => {
    const { page, size } = filtersValues;
    getingCancelatiionTask({
      ...filtersValues,
      page,
      size,
    });
  };

  const setupFilters = () => {
    Can({
      role: authContext.user.role,
      perform: 'orders:filtersByCenterId',
      yes: () => {
        setFilters([
          {
            type: 'input',
            id: 'orderId.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },

          {
            type: 'input',
            id: 'created.in',
            label: 'تاریخ ایجاد',
            icon: mdiCalendar,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت',
            icon: mdiStateMachine,
            data: ['CREATED', 'DONE'],
            translateType: 'cancelTask',
          },
          {
            type: 'checkbox',
            id: 'paymentType.in',
            label: 'شیوه پرداخت',
            icon: mdiStateMachine,
            data: ['COD', 'ONLINE'],
            translateType: 'cod',
          },
        ]);
      },
      no: () => {
        setFilters([
          {
            type: 'input',
            id: 'orderId.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },

          {
            type: 'input',
            id: 'created.in',
            label: 'تاریخ ایجاد',
            icon: mdiCalendar,
          },
          {
            type: 'input',
            id: 'partnerName.contains',
            label: 'نام شرکت',
            icon: mdiAccountOutline,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت',
            icon: mdiStateMachine,
            data: ['CREATED', 'DONE'],
            translateType: 'cancelTask',
          },
          {
            type: 'checkbox',
            id: 'paymentType.in',
            label: 'شیوه پرداخت',
            icon: mdiStateMachine,
            data: ['COD', 'ONLINE'],
            translateType: 'cod',
          },
        ]);
      },
    });
  };
  let headCells = [];

  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        { type: 'input', id: 'orderId', label: 'شناسه سفارش' },
        {
          id: 'unLocateItemsCount',
          label: 'تعداد آیتم های سفارش',
          type: 'input',
        },
        {
          id: 'itemsCount',
          label: 'تعداد آیتم ها',
          type: 'input',
        },
        {
          id: 'orderLastStatus',
          label: 'وضعیت قبلی',
          type: 'checkbox',
          data: [
            'ORDERED',
            'DELIVERED_TO_POST',
            'ASSIGNED',
            'PICKED',
            'PACKAGED',
            'BUNDLED',
            'LOADED',
            'ARRIVED',
            'DELIVERED',
            'NOT_DELIVERED',
            'RETURNED',
            'CANCELLED',
          ],
          translateType: 'Order',
        },
        {
          id: 'created',
          label: 'تاریخ ایجاد',
          type: 'date-picker',
        },
        {
          id: 'status',
          label: 'وضعیت',
          type: 'checkbox',
          data: ['CREATED', 'DONE'],
          translateType: 'CancellationTasks',
        },
        {
          type: 'checkbox',
          id: 'paymentType',
          label: 'شیوه پرداخت',
          data: ['COD', 'ONLINE'],
          translateType: 'COD',
        },
      ];
    },
    no: () => {
      headCells = [
        { type: 'input', id: 'orderId', label: 'شناسه سفارش' },
        {
          id: 'unLocateItemsCount',
          label: 'تعداد آیتم های سفارش',
          type: 'input',
        },
        {
          id: 'itemsCount',
          label: 'تعداد آیتم ها',
          type: 'input',
        },
        {
          id: 'partnerName',
          label: 'نام شرکت',
          type: 'input',
        },
        {
          id: 'orderLastStatus',
          label: 'وضعیت قبلی',
          type: 'checkbox',
          data: [
            'ORDERED',
            'DELIVERED_TO_POST',
            'ASSIGNED',
            'PICKED',
            'PACKAGED',
            'BUNDLED',
            'LOADED',
            'ARRIVED',
            'DELIVERED',
            'NOT_DELIVERED',
            'RETURNED',
            'CANCELLED',
          ],
          translateType: 'Order',
        },
        {
          id: 'created',
          label: 'تاریخ ایجاد',
          type: 'date-picker',
        },
        {
          id: 'status',
          label: 'وضعیت',
          type: 'checkbox',
          data: ['CREATED', 'DONE'],
          translateType: 'CancellationTasks',
        },
        {
          type: 'checkbox',
          id: 'paymentType',
          label: 'شیوه پرداخت',
          data: ['COD', 'ONLINE'],
          translateType: 'COD',
        },
      ];
    },
  });
  const head = { headCells };
  const updateAllOrderAssign = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { callback, pagination } =
        await cancellationTasksRepository.updateCancellationTaskOperator({
          payload,
        });
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const changeStatusOrderWhitId = (data: any) => {
    const { ids, operatorId, description } = data;
    const body = operatorId;
    const callback = () => {
      fetchUsers();
      getOrders();
    };
    updateAllOrderAssign({
      id: ids,
      body,
      callback,
    });
    setOpenConfirmUpdateState(false);
    setOrderUpdate(!orderUpdate);
  };

  const handleOpenConfirmCancele = (id: number) => {
    setOpenConfirmCancel(true);
    setId(id);
  };
  const handleOpenConfirmUpdateState = (data: { id: number; body: Status }) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
  };
  const convertArrayItemsToString = () => {
    const array = allOrders;
    const reducer = (total: any, currentValue: any) => total + currentValue;
    const newArray = array?.map(obj => {
      return {
        ...obj,
        items: obj.items.reduce(reducer).productName,
      };
    });
    return newArray;
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
      'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const findOrderStatus = (id: number) => {
    const status = ordersList.find(order => order.id === id)?.status;
    return status && translateOrderStatus(status);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };
  const getUsers = async (actions: any) => {
    const payload = { ...actions };
    try {
      setUsersLoading(true);
      const { response, count } = await usersRepository.getUsers({ payload });
      setUsers(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setUsersLoading(false);
    }
  };
  const fetchUsers = () => {
    const { page, size } = filtersValues;
    getUsers({
      role: { equals: 'WAREHOUSE_OPERATOR' },
      with: 'orders-count',
      page: 0,
      size: 100,
    });
  };

  return (
    <AuthConsumer>
      {({ user }) => (
        <div className={classes.root}>
          <Grid container spacing={5}>
            <Grid container item xs={12} sm={6} alignItems="center">
              <Typography
                variant="h4"
                component="div"
                style={{ marginLeft: '20px', fontSize: '18px' }}
              >
                {'لیست سفارش‌ها'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderFilter
                filters={filters}
                onFilter={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    page: 0,
                    size: 100,
                  });
                }}
                mainFiltersCount={0}
                baseFilter={{
                  type: 'input',
                  id: 'orderId.equals',
                  placeholder: 'شناسه سفارش',
                }}
                filterValues={filtersValues}
              />
            </Grid>
            <Grid xs={12} sm={6} item container alignItems="center">
              {loading ? (
                ''
              ) : (
                <>
                  {ordersList.length > 0 && (
                    <>
                      <div className={classes.subtitle}>
                        {'تعداد کل سفارش‌ها:'}
                      </div>
                      <div>
                        &nbsp;
                        <b>{`${count}`}</b>
                      </div>
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {loading ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {ordersList.length > 0 ? (
                    <List
                      data={ordersList}
                      users={users}
                      loading={usersLoading}
                      // handleConfirmCanceleBox={handleOpenConfirmCancele}
                      changeStatusOrderWhitId={changeStatusOrderWhitId}
                      // handleOpenConfirmUpdateState={
                      //   handleOpenConfirmUpdateState
                      // }
                      head={head}
                      filterVal={filtersValues}
                      onFilterValue={(filtervalues: any) => {
                        setFiltersValues({
                          ...removeEmptyValueObject(filtervalues),
                          ...filtersValues,
                          page: 0,
                          size: 10,
                        });
                      }}
                      removeFilterHandler={removeFilterHandler}
                      filterLabelTranslator={filterTranslator}
                    />
                  ) : (
                    <Typography variant="h3" component="div">
                      سفارشی موجود نیست
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {ordersList.length > 0 && (
                <FormControl
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      marginLeft: '10px',
                      color: '#8a8a8a',
                    }}
                  >
                    {' '}
                    تعداد نمایش در هر صفحه:
                  </div>
                  <Select
                    open={openSize}
                    onClose={handleClose}
                    onOpen={() => setOpenSize(true)}
                    value={filtersValues.size}
                    onChange={handleChangeSizePage}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid xs={12} sm={6} container item direction="row-reverse">
              {ordersList.length > 0 && (
                <Pagination
                  count={Math.ceil(count / filtersValues.size)}
                  color="secondary"
                  page={filtersValues.page + 1}
                  onChange={handleChangePage}
                />
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </AuthConsumer>
  );
};

export default Orders;
